import API_BASE from "../config.js";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { Dica, BotaoDica } from "../components/Dica";


// Extrai tenant_id do JWT token - evita conflito entre tenants
function getTenantIdFromToken() {
  try {
    const token = (sessionStorage.getItem("access_token")||localStorage.getItem("access_token"));
    if (!token) return (sessionStorage.getItem("tenant_id")||localStorage.getItem("tenant_id")) || "1";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return String(payload.tenant_id || (sessionStorage.getItem("tenant_id")||localStorage.getItem("tenant_id")) || "1");
  } catch { return (sessionStorage.getItem("tenant_id")||localStorage.getItem("tenant_id")) || "1"; }
}

// GET /config/licenca_tenant_{id} e /config/features_liberadas_tenant_{id}
// passaram a exigir autenticacao (chaves carregam dado de uma oficina
// especifica -- ver auditoria de 11/08/2026). Sem este header as chamadas
// abaixo passam a voltar 401.
function getAuthHeaders() {
  const token = sessionStorage.getItem("access_token") || localStorage.getItem("access_token") || "";
  return token ? { Authorization: "Bearer " + token } : {};
}

const WHATSAPP = "5511958325430";
const API      = "" + API_BASE + "/api/v1/config";

// Plano "Profissional" descontinuado (auditoria de 18/08/2026) -- os
// recursos que eram exclusivos dele passaram a fazer parte do Premium.
const CORES = {
  basico:  { cor:"#6366f1", bg:"#6366f115", borda:"#6366f1" },
  premium: { cor:"#f59e0b", bg:"#f59e0b15", borda:"#f59e0b" },
};
const INCLUSOS_PADRAO = {
  basico: {
    inclusos: ["feat_ate_20_os","feat_ate_15_clientes","feat_ate_30_itens_estoque","feat_busca_interna","feat_orcamento_pdf_basico","feat_suporte_email"],
    bloqueados: ["feat_os_ilimitadas","feat_busca_ia_estoque","feat_relatorios_avancados","feat_logo_orcamento","feat_fundo_personalizado","feat_tipos_orcamento_extras","feat_multi_usuario","feat_personalizacao_completa","feat_checklist","feat_consulta_placa_detran","feat_api_maquininhas"]
  },
  premium: {
    inclusos: ["feat_tudo_do_basico","feat_os_ilimitadas","feat_clientes_ilimitados","feat_estoque_ilimitado","feat_busca_ia_estoque","feat_relatorios_avancados","feat_logo_orcamento","feat_fundo_personalizado","feat_tipos_orcamento_extras","feat_exportar_csv_html","feat_multi_usuario","feat_personalizacao_completa","feat_orcamento_com_fotos","feat_checklist","feat_suporte_whatsapp","feat_api_maquininhas","feat_consulta_placa_detran","feat_assinatura_digital"],
    bloqueados: []
  },
};

function fmtPreco(v) { return "R$ " + Number(v).toLocaleString("pt-BR",{minimumFractionDigits:2}); }
function fmtData(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return ""; }
}

// Mapeia o nome da ROTA no backend (POST /billing/{tipo}) pro radical usado
// nas chaves de traducao (meu_plano.{radical}_titulo, sim_{radical}, etc.) --
// evita repetir a mesma cadeia de ternarios em varios pontos do JSX abaixo.
const ACAO_ASSINATURA_LABEL = { cancel: "cancelar", pause: "pausar", resume: "retomar" };

function waLink(planoAtual, planoDesejado, preco, implantacao, anual, mesesDesc) {
  let msg = "Ola! Sou cliente do SAAS.GL.\n\nPlano atual: " + planoAtual + "\nQuero o plano: " + planoDesejado;
  if (anual) {
    const total = preco * (12 - mesesDesc);
    msg += "\n\nCobranca: Anual a vista\n- Implantacao: " + fmtPreco(implantacao) + " (unica vez)\n- Anual: " + fmtPreco(total) + " (" + (12-mesesDesc) + "x " + fmtPreco(preco) + " — " + mesesDesc + " meses gratis)\n- Economia: " + fmtPreco(preco * mesesDesc);
  } else {
    msg += "\n\nCobranca: Mensal\n- Implantacao: " + fmtPreco(implantacao) + " (unica vez)\n- Mensalidade: " + fmtPreco(preco) + "/mes";
  }
  msg += "\n\nPoderia me ajudar?";
  return "https://wa.me/" + WHATSAPP + "?text=" + encodeURIComponent(msg);
}

function waLinkAdicional(planoAtual, nomeAdicional, preco) {
  const msg = "Ola! Sou cliente do SAAS.GL.\n\nPlano atual: " + planoAtual + "\nGostaria de solicitar o adicional: " + nomeAdicional + "\nValor: +" + fmtPreco(preco) + "/mes\n\nPoderia me ajudar a liberar?";
  return "https://wa.me/" + WHATSAPP + "?text=" + encodeURIComponent(msg);
}

export default function MeuPlano() {
  const { t } = useTranslation();
  const [planosAPI, setPlanosAPI] = useState(null);
  const [loading, setLoading]     = useState(true);
  // AUD-X2c -- valor combinado para ESTA oficina (chave preco_tenant_{id}).
  // null = nao ha valor proprio, vale o preco do plano.
  const [precoProprio, setPrecoProprio] = useState(null);

  useEffect(() => {
    fetch(API + "/planos")
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && d.value) {
          const arr = Array.isArray(d.value) ? d.value : Object.values(d.value);
          const obj = {};
          arr.forEach(p => { obj[(p.id||p.nome||"").toLowerCase()] = p; });
          setPlanosAPI(obj);
        }
      })
      .catch(()=>{})
      .finally(()=>setLoading(false));
  }, []);

  const [planoKey, setPlanoKey] = useState(((sessionStorage.getItem("plano")||localStorage.getItem("plano")) || "basico").toLowerCase());
  const [dias, setDias] = useState(30);
  const [adicionais, setAdicionais] = useState({});
  const [featuresLiberadas, setFeaturesLiberadas] = useState([]);
  const [assinando, setAssinando] = useState(null);
  // Contratacao automatica de adicionais (cobranca proporcional pelos dias
  // que faltam ate a proxima renovacao -- ver src/services/adicionais.py
  // no tenant-service). ativandoAdicional guarda o id em andamento (so um
  // por vez); erroAdicionais mantem uma mensagem por id, usada pra mostrar
  // o fallback manual via WhatsApp so pro adicional que de fato falhou.
  const [ativandoAdicional, setAtivandoAdicional] = useState(null);
  const [erroAdicionais, setErroAdicionais] = useState({});

  // Fluxo de troca de plano -- por padrao a tela mostra so o plano atual;
  // "Trocar plano" revela os outros planos disponiveis, e escolher um
  // deles pede confirmacao antes de chamar /billing/change-plan (nunca
  // troca direto sem o cliente confirmar).
  const [mostrarTrocar, setMostrarTrocar] = useState(false);
  const [planoAlvo, setPlanoAlvo]         = useState(null);
  const [trocando, setTrocando]           = useState(false);
  const [erroTroca, setErroTroca]         = useState("");
  const [trocaSucesso, setTrocaSucesso]   = useState(false);

  // Estado da assinatura recorrente (GET /billing/status) -- null enquanto
  // o tenant so tem o trial automatico (nunca cadastrou cartao/Pix no MP,
  // entao nao ha o que cancelar/pausar). Quando existe: {estado: "ativa" |
  // "pausada" | "cancelada", preapproval_id, plano}. dataExpiracaoISO vem
  // do mesmo fetch (licenca.dataExpiracao) -- usado pra mostrar "acesso
  // garantido ate DD/MM/AAAA" nas confirmacoes de pausar/cancelar (o
  // cancelamento/pausa nunca mexe nessa data, so no que o MP cobra dai pra
  // frente -- ver POST /billing/cancel e /pause no tenant-service).
  const [assinatura, setAssinatura]           = useState(null);
  const [dataExpiracaoISO, setDataExpiracaoISO] = useState(null);
  // acaoAssinatura guarda qual acao esta em confirmacao no modal: "cancel" |
  // "pause" | "resume" | null (modal fechado). Mesmo padrao do modal de
  // troca de plano acima (planoAlvo / confirmarTroca / fecharModalTroca).
  const [acaoAssinatura, setAcaoAssinatura] = useState(null);
  const [executandoAcao, setExecutandoAcao] = useState(false);
  const [erroAcao, setErroAcao]             = useState("");
  const [acaoConcluida, setAcaoConcluida]   = useState(null);

  const carregarStatusAssinatura = () => {
    fetch(API_BASE + "/api/v1/billing/status", { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setAssinatura(d.assinatura || null);
        if (d.licenca && d.licenca.dataExpiracao) setDataExpiracaoISO(d.licenca.dataExpiracao);
      })
      .catch(() => {});
  };

  useEffect(() => { carregarStatusAssinatura(); }, []);

  // Cancela, pausa ou retoma a assinatura -- POST /billing/{tipo}, sem
  // corpo (o tenant e identificado pelo token, igual aos outros endpoints
  // de billing). Em caso de sucesso, recarrega a pagina (mesmo padrao do
  // confirmarTroca acima) pra tudo -- estado da assinatura, banner, dias
  // restantes -- vir sempre fresco do backend, sem risco de a tela ficar
  // com um estado local desatualizado.
  const executarAcaoAssinatura = async (tipo) => {
    setExecutandoAcao(true);
    setErroAcao("");
    try {
      const r = await fetch(API_BASE + "/api/v1/billing/" + tipo, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErroAcao(d.detail || t("meu_plano.erro_acao_assinatura"));
        setExecutandoAcao(false);
        return;
      }
      setAcaoConcluida(tipo);
      setTimeout(() => window.location.reload(), 1500);
    } catch {
      setErroAcao(t("meu_plano.erro_acao_assinatura"));
      setExecutandoAcao(false);
    }
  };

  const fecharModalAssinatura = () => {
    if (executandoAcao) return;
    setAcaoAssinatura(null);
    setErroAcao("");
    setAcaoConcluida(null);
  };

  // Assinatura recorrente via Mercado Pago -- cria a assinatura no backend
  // (POST /billing/subscribe) e redireciona pro checkout hospedado do MP,
  // onde o cliente cadastra o cartao. A liberacao real da licenca so
  // acontece quando o webhook confirmar o pagamento (nao neste redirect),
  // entao o cliente pode ver "aguardando confirmacao" por alguns segundos
  // depois de voltar do checkout.
  const assinar = async (chave) => {
    setAssinando(chave);
    try {
      const r = await fetch(API_BASE + "/api/v1/billing/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ plano: chave }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.checkout_url) {
        window.location.href = d.checkout_url;
        return;
      }
      alert(d.detail || t("meu_plano.erro_assinatura"));
    } catch {
      alert(t("meu_plano.erro_conexao_assinatura"));
    }
    setAssinando(null);
  };

  // Troca de plano numa assinatura ja existente -- POST /billing/change-plan.
  // No backend: se ja existe assinatura ativa no MP, so atualiza o valor
  // (sem checkout novo); se nao existe ainda (ex: cliente ainda no trial
  // do basico), o backend cai no mesmo fluxo de /subscribe e devolve um
  // checkout_url, que tratamos aqui do mesmo jeito que assinar().
  const confirmarTroca = async () => {
    if (!planoAlvo) return;
    setTrocando(true); setErroTroca("");
    try {
      const r = await fetch(API_BASE + "/api/v1/billing/change-plan", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ plano: planoAlvo }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.checkout_url) {
        window.location.href = d.checkout_url;
        return;
      }
      if (r.ok && d.ok) {
        setTrocaSucesso(true);
        setTimeout(() => window.location.reload(), 1500);
        return;
      }
      setErroTroca(d.detail || t("meu_plano.erro_trocar_plano"));
    } catch {
      setErroTroca(t("meu_plano.erro_conexao"));
    }
    setTrocando(false);
  };

  const fecharModalTroca = () => {
    if (trocando) return;
    setPlanoAlvo(null);
    setErroTroca("");
    setTrocaSucesso(false);
  };

  // Contrata um adicional (ex: checklist) via POST /billing/adicionais/{id}/ativar
  // -- o backend decide se cobra proporcional (devolve checkout_url, o
  // navegador vai pro Mercado Pago completar o pagamento) ou libera na
  // hora sem custo (trial ainda nao pago / plano ja incluso). Falhando por
  // qualquer motivo (Mercado Pago nao configurado, rede, etc.), guarda o
  // erro so pra ESTE id -- o botao vira o link manual de WhatsApp
  // (waLinkAdicional), sem travar os outros adicionais da lista.
  const ativarAdicionalAutomatico = async (id, ad) => {
    setAtivandoAdicional(id);
    setErroAdicionais(prev => ({ ...prev, [id]: "" }));
    try {
      const BILLING_API = API.replace(/\/config$/, "/billing");
      const r = await fetch(BILLING_API + "/adicionais/" + id + "/ativar", {
        method: "POST",
        headers: getAuthHeaders(),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErroAdicionais(prev => ({ ...prev, [id]: d.detail || t("meu_plano.erro_ativar_adicional") }));
        setAtivandoAdicional(null);
        return;
      }
      if (d.checkout_url) {
        window.location.href = d.checkout_url;
        return;
      }
      window.location.reload();
    } catch {
      setErroAdicionais(prev => ({ ...prev, [id]: t("meu_plano.erro_conexao") }));
      setAtivandoAdicional(null);
    }
  };

  useEffect(() => {
    const tenantId = getTenantIdFromToken() || "1";
    fetch(API + "/licenca_tenant_" + tenantId, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && d.value) {
          const plano = (d.value.plano || "basico").toLowerCase();
          setPlanoKey(plano);
          sessionStorage.setItem("plano", plano);
          const exp = new Date(d.value.dataExpiracao);
          const diffMs = exp - new Date();
          setDias(Math.max(0, Math.ceil(diffMs / (1000*60*60*24))));
        }
      }).catch(() => {});
  }, []);

  // AUD-X2c -- le o valor combinado desta oficina. A chave e protegida por
  // tenant (ver config.py, _TENANT_SCOPED_PREFIXES), e o token da propria
  // oficina da acesso ao proprio id -- por isso getAuthHeaders(), como a
  // licenca ja fazia. Qualquer falha e silenciosa: a tela simplesmente
  // segue mostrando o preco do plano, que e o comportamento de antes.
  useEffect(() => {
    const tenantId = getTenantIdFromToken() || "1";
    fetch(API + "/preco_tenant_" + tenantId, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        let v = d && d.value;
        if (v && typeof v === "object") v = v.preco;
        const n = Number(v);
        if (v !== null && v !== undefined && typeof v !== "boolean" && isFinite(n) && n > 0) {
          setPrecoProprio(n);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const tenantId = getTenantIdFromToken() || "1";
    fetch(API + "/adicionais")
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.value) setAdicionais(d.value); })
      .catch(() => {});
    fetch(API + "/features_liberadas_tenant_" + tenantId, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.value && d.value.features) setFeaturesLiberadas(d.value.features); })
      .catch(() => {});
  }, []);

  const getPlano = (key) => {
    const api  = planosAPI?.[key] || {};
    const cores = CORES[key] || CORES.basico;
    const inc   = INCLUSOS_PADRAO[key] || INCLUSOS_PADRAO.basico;
    return {
      nome:        t(`meu_plano.plano_${key}`, api.nome || key.charAt(0).toUpperCase() + key.slice(1)),
      // AUD-X2c -- o valor combinado vence o preco de tabela, em TODOS os
      // planos. getPlano() alimenta o cartao atual, os cartoes de troca e
      // os links de WhatsApp: sobrescrever aqui e o que impede qualquer
      // canto da tela de mostrar um numero diferente do que sera cobrado.
      preco:       precoProprio != null ? precoProprio : (Number(api.preco) || 0),
      implantacao: Number(api.implantacao) || 0,
      subtitulo:   api.subtitulo   || "",
      testeDias:   api.testeDias   || 0,
      mesesDesc:   Number(api.mesesDesc)   || 2,
      ...cores, ...inc,
    };
  };

  const plano    = getPlano(planoKey);
  const outrosPlanos = ["basico","premium"].filter(k => k !== planoKey);
  const urg      = planoKey === "basico" && dias <= 7;

  const Check = ({cor}) => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={cor} strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>;
  const Lock  = ()      => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;

  if (loading) return <Layout><div style={{color:"var(--text2)",padding:40,textAlign:"center"}}>{t("meu_plano.carregando")}</div></Layout>;

  return (
    <Layout>
      <div style={{display:"flex",flexDirection:"column",gap:24,maxWidth:960,margin:"0 auto"}}>

        {/* Header */}
        <div style={{alignItems:"flex-end",display:"flex",justifyContent:"space-between"}}>
          <div>
            <div style={{alignItems:"center",display:"flex",gap:8}}><h1 style={{fontSize:22,fontWeight:700}}>{t("meu_plano.titulo")}</h1><BotaoDica id="meu_plano" /></div>
            <p style={{color:"var(--text2)",fontSize:13,marginTop:3}}>{t("meu_plano.subtitulo")}</p>
          </div>
          <div style={{alignItems:"center",background:plano.bg,border:"1px solid "+plano.borda+"44",borderRadius:12,display:"flex",gap:10,padding:"10px 18px"}}>
            <div>
              <div style={{color:plano.cor,fontSize:13,fontWeight:800}}>{t("meu_plano.plano_label",{nome:plano.nome})}</div>
              <div style={{color:"var(--text3)",fontSize:11}}>{dias > 0 ? t("meu_plano.dias_restantes",{count:dias}) : t("meu_plano.expirado")}</div>
            </div>
          </div>
        </div>

        <Dica id="meu_plano" jaUsou={false} texto={t("dicas.meu_plano")} />

        {/* Banner basico */}
        {planoKey === "basico" && (
          <div style={{alignItems:"center",background:urg?"#ef444415":"#6366f115",border:"2px solid "+(urg?"#ef4444":"#6366f1"),borderRadius:14,display:"flex",gap:16,justifyContent:"space-between",padding:"18px 24px"}}>
            <div style={{alignItems:"center",display:"flex",gap:14}}>
              <div style={{fontSize:32}}>{urg?"⚠️":"🎉"}</div>
              <div>
                <div style={{color:urg?"#ef4444":"#818cf8",fontSize:15,fontWeight:800,marginBottom:3}}>
                  {dias > 0 ? t("meu_plano.teste_expira_em",{count:dias}) : t("meu_plano.teste_expirou")}
                </div>
                <div style={{color:"var(--text2)",fontSize:13}}>
                  {t("meu_plano.teste_desc",{
                    preco: fmtPreco(plano.preco),
                    implantacao: plano.implantacao > 0
                      ? t("meu_plano.implantacao_unica",{valor:fmtPreco(plano.implantacao)})
                      : t("meu_plano.sem_taxa_implantacao"),
                  })}
                </div>
              </div>
            </div>
            <div style={{alignItems:"center",background:urg?"#ef4444":"#6366f1",borderRadius:50,color:"#fff",display:"flex",flexShrink:0,fontSize:22,fontWeight:900,height:60,justifyContent:"center",width:60}}>{dias}</div>
          </div>
        )}

        {/* Card plano atual */}
        <div style={{background:"var(--bg3)",border:"3px solid "+plano.borda,borderRadius:18,overflow:"hidden"}}>
          <div style={{background:"linear-gradient(90deg,"+plano.cor+","+plano.cor+"99)",height:6}} />
          <div style={{padding:"24px 28px"}}>
            <div style={{alignItems:"flex-start",display:"flex",justifyContent:"space-between",marginBottom:20}}>
              <div>
                <div style={{alignItems:"center",display:"flex",gap:10,marginBottom:8}}>
                  <div style={{background:plano.cor+"22",borderRadius:8,color:plano.cor,fontSize:14,fontWeight:800,padding:"4px 14px"}}>{t("meu_plano.plano_label",{nome:plano.nome})}</div>
                  {planoKey === "basico" && dias > 0 && <span style={{background:"#6366f115",borderRadius:6,color:dias>7?"#818cf8":"#ef4444",fontSize:12,fontWeight:600,padding:"3px 10px"}}>{t("meu_plano.dias_restantes",{count:dias})}</span>}
                        <span style={{background:"#22c55e15",borderRadius:6,color:"#22c55e",fontSize:12,fontWeight:700,padding:"6px 14px"}}>{t("meu_plano.ativo")}</span>
                </div>
                <div style={{color:plano.cor,fontSize:30,fontWeight:900}}>
                  {fmtPreco(plano.preco)}<span style={{color:"var(--text2)",fontSize:14,fontWeight:400,marginLeft:8}}>/{t("meu_plano.mes")}</span>
                </div>
                {plano.implantacao > 0 && <div style={{color:"var(--text3)",fontSize:13,marginTop:4}}>{t("meu_plano.implantacao_cobrada",{valor:fmtPreco(plano.implantacao)})}</div>}
                {/* AUD-X2c -- diz de onde vem o numero acima quando ele nao
                    e o preco de tabela, pra nao parecer erro de sistema. */}
                {precoProprio != null && <div style={{color:"var(--text3)",fontSize:13,marginTop:4}}>{t("meu_plano.valor_combinado", "Valor combinado para a sua oficina.")}</div>}
              </div>
              <div style={{display:"flex",flexShrink:0,gap:8}}>
                {planoKey !== "basico" && (
                  <button onClick={()=>assinar(planoKey)} disabled={assinando===planoKey}
                    style={{alignItems:"center",background:plano.cor,border:"none",borderRadius:8,color:"#fff",cursor:assinando===planoKey?"default":"pointer",display:"flex",flexShrink:0,fontSize:13,fontWeight:700,gap:6,padding:"10px 18px"}}>
                    {assinando===planoKey ? t("meu_plano.abrindo_checkout") : (dias<=7 ? t("meu_plano.renovar_agora") : t("meu_plano.gerenciar_assinatura"))}
                  </button>
                )}
                <button onClick={()=>setMostrarTrocar(v=>!v)}
                  style={{alignItems:"center",background:"transparent",border:"1.5px solid "+plano.cor,borderRadius:8,color:plano.cor,cursor:"pointer",display:"flex",flexShrink:0,fontSize:13,fontWeight:700,padding:"10px 18px"}}>
                  {mostrarTrocar ? t("meu_plano.ocultar_planos") : t("meu_plano.trocar_plano")}
                </button>
              </div>
            </div>
            <div style={{display:"grid",gap:24,gridTemplateColumns:"1fr 1fr"}}>
              <div>
                <div style={{color:"var(--text2)",fontSize:11,fontWeight:700,letterSpacing:"0.08em",marginBottom:12,textTransform:"uppercase"}}>✅ {t("meu_plano.inclusos")}</div>
                {plano.inclusos.map(r=><div key={r} style={{alignItems:"center",display:"flex",gap:8,marginBottom:8}}><Check cor={plano.cor}/><span style={{fontSize:13}}>{t(`meu_plano.${r}`)}</span></div>)}
              </div>
              {plano.bloqueados.length > 0 && (
                <div>
                  <div style={{color:"var(--text3)",fontSize:11,fontWeight:700,letterSpacing:"0.08em",marginBottom:12,textTransform:"uppercase"}}>🔒 {t("meu_plano.disponiveis_planos_superiores")}</div>
                  {plano.bloqueados.map(r=><div key={r} style={{alignItems:"center",display:"flex",gap:8,marginBottom:7}}><Lock/><span style={{color:"var(--text3)",fontSize:12}}>{t(`meu_plano.${r}`)}</span></div>)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Trocar de plano -- oculto por padrao; "Trocar plano" no card
            acima revela os outros planos disponiveis (upgrade OU downgrade).
            Escolher um deles abre a confirmacao antes de chamar
            /billing/change-plan (ver confirmarTroca). */}
        {mostrarTrocar && outrosPlanos.length > 0 && (
          <div>
            <div style={{color:"var(--text2)",fontSize:13,fontWeight:600,marginBottom:16}}>
              {t("meu_plano.escolha_novo_plano")}
            </div>
            <div style={{display:"grid",gap:16,gridTemplateColumns:outrosPlanos.length>1?"1fr 1fr":"1fr"}}>
              {outrosPlanos.map(key=>{
                const pl = getPlano(key);
                const destaque = key==="premium";
                return (
                  <div key={key} style={{background:"var(--bg3)",border:"2px solid "+(destaque?pl.borda:"var(--border)"),borderRadius:14,overflow:"hidden",position:"relative"}}>
                    {destaque&&<div style={{background:pl.cor,borderRadius:"0 12px 0 8px",color:"#000",fontSize:10,fontWeight:800,padding:"3px 10px",position:"absolute",right:0,top:0}}>{t("meu_plano.recomendado")}</div>}
                    <div style={{background:"linear-gradient(90deg,"+pl.cor+","+pl.cor+"66)",height:4}} />
                    <div style={{padding:"20px 22px"}}>
                      <div style={{alignItems:"center",display:"flex",gap:8,marginBottom:8}}>
                        <span style={{background:pl.cor+"22",borderRadius:6,color:pl.cor,fontSize:12,fontWeight:700,padding:"3px 10px"}}>{pl.nome}</span>
                      </div>
                      <div style={{color:pl.cor,fontSize:24,fontWeight:800,marginBottom:2}}>
                        {fmtPreco(pl.preco)}<span style={{color:"var(--text2)",fontSize:13,fontWeight:400}}>/{t("meu_plano.mes")}</span>
                      </div>
                      {pl.implantacao > 0 && <div style={{color:"var(--text3)",fontSize:12,marginTop:4,marginBottom:12}}>{t("meu_plano.implantacao_primeira",{valor:fmtPreco(pl.implantacao)})}</div>}
                      {pl.inclusos.map(r=><div key={r} style={{alignItems:"center",display:"flex",gap:8,marginBottom:6,marginTop:pl.implantacao>0?0:12}}><Check cor={pl.cor}/><span style={{color:"var(--text2)",fontSize:12}}>{t(`meu_plano.${r}`)}</span></div>)}
                      <button onClick={()=>setPlanoAlvo(key)}
                        style={{alignItems:"center",background:pl.cor,border:"none",borderRadius:8,color:key==="premium"?"#000":"#fff",cursor:"pointer",display:"flex",fontSize:13,fontWeight:700,justifyContent:"center",marginTop:16,padding:"11px 16px",width:"100%"}}>
                        {t("meu_plano.escolher_plano",{nome:pl.nome})}
                      </button>
                      <a href={waLink(plano.nome,pl.nome,pl.preco,pl.implantacao,false,pl.mesesDesc)} target="_blank" rel="noopener noreferrer"
                        style={{alignItems:"center",background:"transparent",border:"1px solid "+pl.cor+"66",borderRadius:8,color:pl.cor,display:"flex",fontSize:12,fontWeight:600,justifyContent:"center",marginTop:8,padding:"9px 16px",textDecoration:"none",width:"100%"}}>
                        {t("meu_plano.falar_whatsapp")}
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Modal de confirmacao da troca de plano */}
        {planoAlvo && (
          <div onClick={fecharModalTroca} style={{position:"fixed",inset:0,background:"rgba(5,6,10,0.72)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div onClick={e=>e.stopPropagation()} style={{background:"var(--bg3)",border:"1px solid var(--border)",borderRadius:16,maxWidth:420,width:"100%",padding:28}}>
              {!trocaSucesso ? (
                <>
                  <div style={{fontWeight:800,fontSize:18,marginBottom:10}}>{t("meu_plano.trocar_de_plano_titulo")}</div>
                  <div style={{color:"var(--text2)",fontSize:14,marginBottom:20,lineHeight:1.5}}>
                    {t("meu_plano.trocar_confirmacao",{
                      atual: plano.nome,
                      novo: getPlano(planoAlvo).nome,
                      preco: fmtPreco(getPlano(planoAlvo).preco),
                    })}
                  </div>
                  {erroTroca && <div style={{background:"#ef444422",border:"1px solid #ef444455",borderRadius:8,color:"#fca5a5",fontSize:13,marginBottom:16,padding:"10px 14px"}}>{erroTroca}</div>}
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={fecharModalTroca} disabled={trocando}
                      style={{flex:1,background:"transparent",border:"1px solid var(--border)",borderRadius:8,color:"var(--text2)",cursor:trocando?"default":"pointer",fontSize:13,fontWeight:600,padding:"11px"}}>
                      {t("common.cancelar")}
                    </button>
                    <button onClick={confirmarTroca} disabled={trocando}
                      style={{flex:1,background:"var(--accent)",border:"none",borderRadius:8,color:"#fff",cursor:trocando?"default":"pointer",fontSize:13,fontWeight:700,padding:"11px",opacity:trocando?0.7:1}}>
                      {trocando ? t("meu_plano.processando") : t("meu_plano.sim_trocar")}
                    </button>
                  </div>
                </>
              ) : (
                <div style={{textAlign:"center",padding:"10px 0"}}>
                  <div style={{fontSize:40,marginBottom:12}}>✅</div>
                  <div style={{fontWeight:800,fontSize:17,marginBottom:6}}>{t("meu_plano.plano_atualizado")}</div>
                  <div style={{color:"var(--text2)",fontSize:13}}>{t("meu_plano.atualizando_pagina")}</div>
                </div>
              )}
            </div>
          </div>
        )}

          {Object.keys(adicionais).length > 0 && (
            <div>
              <div style={{color:"var(--text2)",fontSize:13,fontWeight:600,marginBottom:16}}>{t("meu_plano.adicionais_disponiveis")}</div>
              <div style={{display:"grid",gap:12,gridTemplateColumns:"1fr"}}>
                {Object.entries(adicionais).map(([id, ad]) => {
                  const ativo = featuresLiberadas.includes(id);
                  const erro = erroAdicionais[id];
                  const emAndamento = ativandoAdicional === id;
                  return (
                    <div key={id} style={{background:"var(--bg3)",border:"1px solid "+(ativo?"#22c55e":"var(--border)"),borderRadius:12,padding:"16px 20px"}}>
                      <div style={{alignItems:"center",display:"flex",justifyContent:"space-between"}}>
                        <div>
                          <div style={{fontSize:14,fontWeight:700,marginBottom:2}}>{ad.label}</div>
                          <div style={{color:"var(--text2)",fontSize:12}}>{ativo ? t("meu_plano.ja_liberado") : t("meu_plano.disponivel_qualquer_plano")}</div>
                        </div>
                        {ativo ? (
                          <span style={{background:"#22c55e15",borderRadius:6,color:"#22c55e",fontSize:12,fontWeight:700,padding:"6px 14px"}}>{t("meu_plano.ativo")}</span>
                        ) : erro ? (
                          <a href={waLinkAdicional(plano.nome, ad.label, ad.preco)} target="_blank" rel="noopener noreferrer"
                            style={{background:"var(--accent)",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,padding:"9px 18px",textDecoration:"none",whiteSpace:"nowrap"}}>
                            {t("meu_plano.solicitar_via_whatsapp")}
                          </a>
                        ) : (
                          <button onClick={() => ativarAdicionalAutomatico(id, ad)} disabled={emAndamento}
                            style={{background:"var(--accent)",border:"none",borderRadius:8,color:"#fff",cursor:emAndamento?"default":"pointer",fontSize:13,fontWeight:700,opacity:emAndamento?0.7:1,padding:"9px 18px",whiteSpace:"nowrap"}}>
                            {emAndamento ? t("meu_plano.processando") : t("meu_plano.solicitar_adicional",{valor:fmtPreco(ad.preco)})}
                          </button>
                        )}
                      </div>
                      {erro && <div style={{color:"#f87171",fontSize:11,marginTop:8}}>{erro}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        {/* Gerenciar assinatura -- so aparece se o tenant ja tem uma
            assinatura de verdade no Mercado Pago (assinatura !== null); no
            trial automatico (sem cartao/Pix cadastrado ainda) nao ha nada
            pra cancelar ou pausar. Pausar e cancelar SO afetam cobrancas
            futuras -- o acesso ja pago continua ate dataExpiracaoISO,
            mostrado no aviso abaixo assim que a assinatura nao esta mais
            "ativa" (ver POST /billing/cancel e /pause no tenant-service). */}
        {assinatura && (
          <div style={{background:"var(--bg3)",border:"1px solid var(--border)",borderRadius:12,padding:20}}>
            <div style={{alignItems:"center",display:"flex",flexWrap:"wrap",gap:16,justifyContent:"space-between"}}>
              <div>
                <div style={{color:"var(--text2)",fontSize:11,fontWeight:700,letterSpacing:"0.08em",marginBottom:10,textTransform:"uppercase"}}>{t("meu_plano.assinatura_titulo")}</div>
                <span style={{
                  background: assinatura.estado==="ativa" ? "#22c55e15" : assinatura.estado==="pausada" ? "#f59e0b15" : "#ef444415",
                  borderRadius:6,
                  color: assinatura.estado==="ativa" ? "#22c55e" : assinatura.estado==="pausada" ? "#f59e0b" : "#ef4444",
                  fontSize:12,fontWeight:700,padding:"6px 14px",
                }}>
                  {t(`meu_plano.assinatura_estado_${assinatura.estado}`)}
                </span>
                {assinatura.estado !== "ativa" && dataExpiracaoISO && (
                  <div style={{color:"var(--text3)",fontSize:12,marginTop:10}}>
                    {t("meu_plano.assinatura_acesso_ate",{data:fmtData(dataExpiracaoISO)})}
                  </div>
                )}
              </div>
              <div style={{display:"flex",flexShrink:0,flexWrap:"wrap",gap:8}}>
                {assinatura.estado === "ativa" && (
                  <button onClick={()=>setAcaoAssinatura("pause")}
                    style={{background:"transparent",border:"1px solid var(--border)",borderRadius:8,color:"var(--text2)",cursor:"pointer",fontSize:13,fontWeight:600,padding:"9px 16px"}}>
                    {t("meu_plano.pausar_assinatura")}
                  </button>
                )}
                {assinatura.estado === "pausada" && (
                  <button onClick={()=>setAcaoAssinatura("resume")}
                    style={{background:"#22c55e",border:"none",borderRadius:8,color:"#fff",cursor:"pointer",fontSize:13,fontWeight:700,padding:"9px 16px"}}>
                    {t("meu_plano.retomar_assinatura")}
                  </button>
                )}
                {assinatura.estado !== "cancelada" && (
                  <button onClick={()=>setAcaoAssinatura("cancel")}
                    style={{background:"transparent",border:"1px solid #ef444466",borderRadius:8,color:"#ef4444",cursor:"pointer",fontSize:13,fontWeight:600,padding:"9px 16px"}}>
                    {t("meu_plano.cancelar_assinatura")}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal de confirmacao de cancelar/pausar/retomar -- mesmo padrao
            visual do modal de troca de plano acima (planoAlvo). O texto de
            cada acao vem das chaves meu_plano.{radical}_titulo/_confirmacao,
            onde {radical} e ACAO_ASSINATURA_LABEL[acaoAssinatura]. */}
        {acaoAssinatura && (
          <div onClick={fecharModalAssinatura} style={{position:"fixed",inset:0,background:"rgba(5,6,10,0.72)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div onClick={e=>e.stopPropagation()} style={{background:"var(--bg3)",border:"1px solid var(--border)",borderRadius:16,maxWidth:420,width:"100%",padding:28}}>
              {!acaoConcluida ? (
                <>
                  <div style={{fontWeight:800,fontSize:18,marginBottom:10}}>
                    {t(`meu_plano.${ACAO_ASSINATURA_LABEL[acaoAssinatura]}_titulo`)}
                  </div>
                  <div style={{color:"var(--text2)",fontSize:14,marginBottom:20,lineHeight:1.5}}>
                    {acaoAssinatura === "resume"
                      ? t("meu_plano.retomar_confirmacao")
                      : t(`meu_plano.${ACAO_ASSINATURA_LABEL[acaoAssinatura]}_confirmacao`,{
                          plano: plano.nome,
                          data: fmtData(dataExpiracaoISO),
                        })}
                  </div>
                  {erroAcao && <div style={{background:"#ef444422",border:"1px solid #ef444455",borderRadius:8,color:"#fca5a5",fontSize:13,marginBottom:16,padding:"10px 14px"}}>{erroAcao}</div>}
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={fecharModalAssinatura} disabled={executandoAcao}
                      style={{flex:1,background:"transparent",border:"1px solid var(--border)",borderRadius:8,color:"var(--text2)",cursor:executandoAcao?"default":"pointer",fontSize:13,fontWeight:600,padding:"11px"}}>
                      {t("common.cancelar")}
                    </button>
                    <button onClick={()=>executarAcaoAssinatura(acaoAssinatura)} disabled={executandoAcao}
                      style={{flex:1,background: acaoAssinatura==="cancel" ? "#ef4444" : "var(--accent)",border:"none",borderRadius:8,color:"#fff",cursor:executandoAcao?"default":"pointer",fontSize:13,fontWeight:700,padding:"11px",opacity:executandoAcao?0.7:1}}>
                      {executandoAcao ? t("meu_plano.processando") : t(`meu_plano.sim_${ACAO_ASSINATURA_LABEL[acaoAssinatura]}`)}
                    </button>
                  </div>
                </>
              ) : (
                <div style={{textAlign:"center",padding:"10px 0"}}>
                  <div style={{fontSize:40,marginBottom:12}}>✅</div>
                  <div style={{fontWeight:800,fontSize:17,marginBottom:6}}>
                    {t(`meu_plano.acao_concluida_${ACAO_ASSINATURA_LABEL[acaoConcluida]}`)}
                  </div>
                  <div style={{color:"var(--text2)",fontSize:13}}>{t("meu_plano.atualizando_pagina")}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Ajuda */}
        <div style={{background:"var(--bg3)",border:"1px solid var(--border)",borderRadius:12,padding:20}}>
          <div style={{color:"var(--text2)",fontSize:11,fontWeight:700,letterSpacing:"0.08em",marginBottom:12,textTransform:"uppercase"}}>{t("meu_plano.precisa_ajuda")}</div>
          <div style={{alignItems:"center",display:"flex",flexWrap:"wrap",gap:16}}>
            <a href={"https://wa.me/"+WHATSAPP+"?text="+encodeURIComponent("Ola! Tenho uma duvida sobre o SAAS.GL.")} target="_blank" rel="noopener noreferrer"
              style={{alignItems:"center",background:"#25D36622",border:"1px solid #25D36640",borderRadius:8,color:"#25D366",display:"flex",fontSize:13,fontWeight:600,gap:8,padding:"10px 16px",textDecoration:"none"}}>
              WhatsApp
            </a>
            <span style={{color:"var(--text3)",fontSize:12}}>{t("meu_plano.respondemos_horario")}</span>
          </div>
        </div>

      </div>
    </Layout>
  );
}
